import {HStack, Stack, Text} from 'platform/foundation';
import styled from 'styled-components';

import {OnlineOfferAlternative} from '../../../api/types/onlineOfferAlternativesTypes';
import {getAlternativeLabel} from '../../../utils/getAlternativeLabel';
import {translateWithFallback as t} from '../../../utils/translateWithFallback';

interface ComparisonToolbarProps {
  alternatives: OnlineOfferAlternative[];
  referenceId: string;
  onReferenceChange: (alternativeId: string) => void;
  isOnlyDifferences: boolean;
  onOnlyDifferencesChange: (isOnlyDifferences: boolean) => void;
  differencesCount: number;
}

export function ComparisonToolbar(props: ComparisonToolbarProps) {
  return (
    <Stack
      direction={['column', 'row']}
      justify="space-between"
      align={['flex-start', 'center']}
      spacing={3}
    >
      <StyledLabel>
        <Text size="small">
          {t('alternatives.comparison.referenceLabel', 'Compare prices against')}
        </Text>
        <StyledSelect
          value={props.referenceId}
          onChange={(event) => props.onReferenceChange(event.target.value)}
          data-testid="comparisonReference"
        >
          {props.alternatives.map((alternative) => (
            <option key={alternative.id} value={alternative.id}>
              {getAlternativeLabel(alternative)}
            </option>
          ))}
        </StyledSelect>
      </StyledLabel>
      <StyledLabel>
        <input
          type="checkbox"
          checked={props.isOnlyDifferences}
          onChange={(event) => props.onOnlyDifferencesChange(event.target.checked)}
          data-testid="comparisonOnlyDifferences"
        />
        <HStack spacing={2} align="center">
          <Text size="small">
            {t('alternatives.comparison.onlyDifferences', 'Only differences')}
          </Text>
          <StyledCount data-testid="comparisonDifferencesCount">
            <Text size="xSmall" color="accent">
              {props.differencesCount}
            </Text>
          </StyledCount>
        </HStack>
      </StyledLabel>
    </Stack>
  );
}

// A long alternative name must not widen a phone screen: the select shrinks instead.
const StyledLabel = styled.label`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  gap: ${({theme}) => theme.getSize(2)};
  cursor: pointer;
`;

const StyledSelect = styled.select`
  min-width: 0;
  max-width: 100%;
  height: ${({theme}) => theme.getSize(10)};
  padding: 0 ${({theme}) => theme.getSize(3)};
  border: 1px solid ${({theme}) => theme.colors.border.emphasisDefault};
  border-radius: ${({theme}) => theme.radii.small};
  font: inherit;
  color: ${({theme}) => theme.colors.text.primary};
  background-color: ${({theme}) => theme.colors.fill.white};
`;

const StyledCount = styled.span`
  padding: 0 ${({theme}) => theme.getSize(2)};
  border-radius: ${({theme}) => theme.radii.xSmall};
  background-color: ${({theme}) => theme.colors.accent.lightest};
`;
